import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Tabs,
  Tab,
} from '@themesberg/react-bootstrap';

const CronSchedulerUI = ({
  value,
  onChange,
  rowId,
  setFocusRowId,
  isFocused,
}) => {
  // Default cron pattern: "0 14 * * 1,3,5" (2:00 PM on Mon, Wed, Fri)
  const defaultCron = '';

  // Initialize schedule state
  const [schedule, setSchedule] = useState(() => {
    if (!value) {
      return {
        desc: '',
        cronExpression: defaultCron,
        startDateTime: '',
        endDateTime: '',
        specificStartTime: '00:00', // Default 12:00 AM
        specificEndTime: '23:59', // Default 11:59 PM
      };
    }
    if (typeof value === 'string') {
      return {
        desc: value,
        cronExpression: value,
        startDateTime: '',
        endDateTime: '',
        specificStartTime: '00:00',
        specificEndTime: '23:59',
      };
    }
    return {
      desc: value.desc,
      cronExpression: value.cronExpression || defaultCron,
      startDateTime: value.startDateTime || '',
      endDateTime: value.endDateTime || '',
      specificStartTime: value.specificStartTime || '00:00',
      specificEndTime: value.specificEndTime || '23:59',
    };
  });

  const [activeTab, setActiveTab] = useState('visual');
  const [timeOption, setTimeOption] = useState(() =>
    value?.specificStartTime && value?.specificEndTime ? 'specific' : 'specific'
  );
  const [startDateTime, setStartDateTime] = useState(
    () => value?.startDateTime || ''
  );
  const [endDateTime, setEndDateTime] = useState(
    () => value?.endDateTime || ''
  );
  const [specificStartTime, setSpecificStartTime] = useState(
    () => value?.specificStartTime || '00:00'
  );
  const [specificEndTime, setSpecificEndTime] = useState(
    () => value?.specificEndTime || '23:59'
  );
  const [frequency, setFrequency] = useState(60);
  const [limitTimeRange, setLimitTimeRange] = useState(true);
  const [startHour, setStartHour] = useState('00');
  const [endHour, setEndHour] = useState('00');
  const [dayOption, setDayOption] = useState('every');
  const [selectedDays, setSelectedDays] = useState([]);

  // Sync state with parent-provided value for editing
  useEffect(() => {
    if (value) {
      const newSchedule = {
        desc: value.desc || '',
        cronExpression: value.cronExpression || defaultCron,
        startDateTime: value.startDateTime || '',
        endDateTime: value.endDateTime || '',
        specificStartTime: value.specificStartTime || '00:00',
        specificEndTime: value.specificEndTime || '23:59',
      };

      setSchedule(newSchedule);

      if (value?.specificStartTime && value?.specificEndTime) {
        setTimeOption('specific');
        setSpecificStartTime(value.specificStartTime);
        setSpecificEndTime(value.specificEndTime);
      } else {
        setTimeOption('specific');
        // Optionally parse cronExpression to set recurring states if needed
      }
    }
  }, [value]);

  // Update cron expression and schedule
  useEffect(() => {
    let minutes, hours, daysOfWeek;
    let newCronExpression;
    if (timeOption === 'specific') {
      const [startHour, startMinute] = specificStartTime.split(':');
      const [endHour, endMinute] = specificEndTime.split(':');
      // minutes = startMinute;
      // hours = startHour;
      daysOfWeek = dayOption === 'every' ? '*' : selectedDays.join(',');
      newCronExpression = `* ${startHour}:${startMinute}-${endHour}:${endMinute} * * ${daysOfWeek}`;
    } else {
      if (frequency === 1) {
        minutes = '*';
      } else if (frequency === 60) {
        minutes = '0';
      } else {
        minutes = `*/${frequency}`;
      }
      hours = limitTimeRange ? `${startHour}-${endHour}:` : '*';
      daysOfWeek = dayOption === 'every' ? '*' : selectedDays.join(',');
      newCronExpression = `${minutes} ${hours} * * ${daysOfWeek}`;
    }

    const newDesc = getDescription();
    const newSchedule = {
      ...schedule,
      cronExpression: newCronExpression,
      specificStartTime: timeOption === 'specific' ? specificStartTime : '',
      specificEndTime: timeOption === 'specific' ? specificEndTime : '',
      startDateTime: startDateTime,
      endDateTime: endDateTime,
      desc: newDesc,
    };
    // console.log(newSchedule);
    setSchedule(newSchedule);
    if (onChange) {
      onChange(newSchedule);
    }
  }, [
    timeOption,
    startDateTime,
    endDateTime,
    specificStartTime,
    specificEndTime,
    frequency,
    limitTimeRange,
    startHour,
    endHour,
    dayOption,
    selectedDays,
  ]);

  // Handle date-time changes
  const handleDateTimeChange = (field, value) => {
    const newSchedule = { ...schedule, [field]: value || '' };
    setSchedule(newSchedule);
    if (onChange) {
      onChange(newSchedule);
    }
  };

  // Toggle days
  const toggleDay = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()
    );
  };

  // Set weekdays or weekends
  const setWeekdays = () => setSelectedDays([1, 2, 3, 4, 5]);
  const setWeekends = () => setSelectedDays([0, 6]);

  // Format hour
  const formatHour = (hour) => {
    const h = parseInt(hour);
    if (h === 0) return '12am';
    if (h === 12) return '12pm';
    return h < 12 ? `${h}am` : `${h - 12}pm`;
  };

  // Format date-time
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Generate description
  function getDescription() {
    let desc = 'Runs';

    if (timeOption === 'specific') {
      desc += ` from ${specificStartTime} to ${specificEndTime}`;
    } else {
      if (frequency === 1) {
        desc += ' every minute';
      } else if (frequency === 60) {
        desc += ' every hour';
      } else {
        desc += ` every ${frequency} minutes`;
      }
      if (limitTimeRange) {
        desc += ` from ${formatHour(startHour)} to ${formatHour(endHour)}`;
      }
    }

    if (dayOption === 'every') {
      desc += ' every day';
    } else {
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const days = selectedDays.map((d) => dayNames[d]).join(', ');
      desc += ` on ${days}`;
    }

    if (startDateTime || endDateTime) {
      desc += ' -';
      if (startDateTime) {
        desc += ` from ${formatDateTime(startDateTime)}`;
      }
      if (endDateTime) {
        desc += ` until ${formatDateTime(endDateTime)}`;
      }
    }

    return desc;
  }

  return (
    <Card
      className="mb-3 p-3"
      onFocus={() => setFocusRowId && setFocusRowId(rowId)}
      style={{ border: isFocused ? '2px solid #007bff' : '1px solid #dee2e6' }}
    >
      <h6 className="mb-2">Pricing Schedule</h6>
      <p className="text-muted mb-3">
        <small>{getDescription()}</small>
      </p>

      {/* Start and End Date-Time Inputs */}
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Start Date and Time</Form.Label>
            <Form.Control
              type="datetime-local"
              value={startDateTime}
              onChange={
                (e) => setStartDateTime(e.target.value)
                // handleDateTimeChange('startDateTime', e.target.value)
              }
            />
            <Form.Text className="text-muted">
              When this schedule begins
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>End Date and Time</Form.Label>
            <Form.Control
              type="datetime-local"
              value={endDateTime}
              onChange={
                (e) => setEndDateTime(e.target.value)
                // handleDateTimeChange('endDateTime', e.target.value)
              }
            />
            <Form.Text className="text-muted">
              When this schedule expires
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        <Tab eventKey="visual" title="Visual Editor">
          {/* Time Selection */}
          <Form.Group className="mb-3">
            <Form.Label>Time</Form.Label>
            <Form.Select
              value={timeOption}
              onChange={(e) => setTimeOption(e.target.value)}
              className="mb-2"
            >
              <option value="specific">Specific time</option>
              {/* <option value="recurring">Recurring</option> */}
            </Form.Select>

            {timeOption === 'specific' && (
              <Row>
                <Col md={6}>
                  <Form.Label>Start Time</Form.Label>
                  <Form.Control
                    type="time"
                    value={specificStartTime}
                    onChange={(e) => setSpecificStartTime(e.target.value)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Label>End Time</Form.Label>
                  <Form.Control
                    type="time"
                    value={specificEndTime}
                    onChange={(e) => setSpecificEndTime(e.target.value)}
                  />
                </Col>
              </Row>
            )}

            {timeOption === 'recurring' && (
              <>
                <Form.Label>Frequency</Form.Label>
                <Form.Select
                  value={frequency}
                  onChange={(e) => setFrequency(Number(e.target.value))}
                  className="mb-2"
                >
                  {[1, 5, 10, 15, 30, 60].map((f) => (
                    <option key={f} value={f}>
                      Every {f} minute{f !== 1 ? 's' : ''}
                    </option>
                  ))}
                </Form.Select>

                <Form.Check
                  type="checkbox"
                  label="Limit to time range"
                  checked={limitTimeRange}
                  onChange={(e) => setLimitTimeRange(e.target.checked)}
                  className="mb-2"
                />

                {limitTimeRange && (
                  <Row>
                    <Col md={6}>
                      <Form.Label>Start Hour</Form.Label>
                      <Form.Select
                        value={startHour}
                        onChange={(e) => setStartHour(e.target.value)}
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i.toString().padStart(2, '0')}>
                            {formatHour(i)}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Label>End Hour</Form.Label>
                      <Form.Select
                        value={endHour}
                        onChange={(e) => setEndHour(e.target.value)}
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i.toString().padStart(2, '0')}>
                            {formatHour(i)}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Row>
                )}
              </>
            )}
          </Form.Group>

          {/* Day Selection */}
          <Form.Group className="mb-3">
            <Form.Label>Days</Form.Label>
            <Form.Select
              value={dayOption}
              onChange={(e) => {
                setDayOption(e.target.value);
                if (e.target.value === 'every') {
                  setSelectedDays([]);
                }
              }}
              className="mb-2"
            >
              <option value="every">Every day</option>
              <option value="specific">Specific days</option>
            </Form.Select>

            {dayOption === 'specific' && (
              <>
                <div className="d-flex gap-1 flex-wrap mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                    (day, index) => (
                      <Button
                        key={day}
                        size="sm"
                        variant={
                          selectedDays.includes(index)
                            ? 'primary'
                            : 'outline-secondary'
                        }
                        onClick={() => toggleDay(index)}
                      >
                        {day}
                      </Button>
                    )
                  )}
                </div>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={setWeekdays}
                  >
                    Weekdays
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={setWeekends}
                  >
                    Weekends
                  </Button>
                </div>
              </>
            )}
          </Form.Group>
        </Tab>

        <Tab eventKey="expression" title="Cron Expression">
          <Form.Group className="mb-3">
            <Form.Label>Cron Expression</Form.Label>
            <Form.Control
              type="text"
              value={schedule.cronExpression || ''}
              onChange={(e) => {
                const newSchedule = {
                  ...schedule,
                  cronExpression: e.target.value,
                };
                setSchedule(newSchedule);
                if (onChange) {
                  onChange(newSchedule);
                }
              }}
              placeholder="* * * * *"
            />
            <Form.Text className="text-muted">
              Format: minute hour day-of-month month day-of-week
            </Form.Text>
          </Form.Group>
        </Tab>
      </Tabs>
    </Card>
  );
};

export default CronSchedulerUI;
